import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { toast } from '@kognitos/lattice';
import { hasManagedOAuthReturn } from '@/modules/credentials/oauth-resume';
import type {
  Bdkv2BookDescriptor,
  V2BookConnectionDescriptor,
} from '@/generated/hooks/bdk/models';
import {
  getExtractAutomationCodeConnectionsQueryKey,
  useForkPublishedAutomation,
  useUpgradeAutomationBookConnectionVersion,
} from '@/generated/hooks/grimoire/v1/api';
import { useConnectionActions } from '@/modules/integrations';
import {
  isConnectionAlreadyAtTargetVersionError,
  isReauthRequiredForUpgrade,
} from '@/modules/integrations/utils';
import { captureException } from '@/modules/sentry';
import { getRoutePath } from '@/routes/util';
import {
  selectCurrentOrganizationId,
  selectCurrentWorkspaceId,
  useUserPreferencesStore,
} from '@/shared/stores/user-preferences.store';

/**
 * sessionStorage key holding the pending connection upgrade intent.
 *
 * Re-authenticating an OAuth connection redirects the browser to the provider,
 * so the upgrade intent must survive the full-page navigation and is resumed
 * once the user returns to the automation.
 */
const UPGRADE_INTENT_STORAGE_KEY = 'connection-upgrade-intent';

interface IConnectionUpgradeIntent {
  automationId: string;
  pendingReauthConnections: Array<{
    connection_id: string;
  }>;
}

// Returns the pending reauth connections stored before the OAuth redirect, or null if none.
const readUpgradeIntent = (): IConnectionUpgradeIntent | null => {
  try {
    const raw = sessionStorage.getItem(UPGRADE_INTENT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<IConnectionUpgradeIntent>;
    if (!parsed.automationId) return null;
    return {
      automationId: parsed.automationId,
      pendingReauthConnections: parsed.pendingReauthConnections ?? [],
    };
  } catch {
    return null;
  }
};

// Written before each OAuth redirect so the resume effect can pick up where
// the upgrade left off after the browser returns from the provider.
const writeUpgradeIntent = (intent: IConnectionUpgradeIntent) => {
  sessionStorage.setItem(UPGRADE_INTENT_STORAGE_KEY, JSON.stringify(intent));
};

// Must be called on both success and failure paths to prevent a stale intent
// from triggering a spurious resume on the next page load.
const clearUpgradeIntent = () => {
  sessionStorage.removeItem(UPGRADE_INTENT_STORAGE_KEY);
};

/**
 * Returns true when an OAuth re-authentication upgrade is pending for the given
 * automation.
 */
export const hasPendingConnectionUpgrade = (automationId: string): boolean => {
  return readUpgradeIntent()?.automationId === automationId;
};

interface IUseUpgradeAutomationConnectionParams {
  automationId: string;
  /** Whether the automation is a draft. Published automations are forked first. */
  isDraft: boolean;
}

interface IUpgradeParams {
  /** The book whose connections should be upgraded. */
  book: string;
  /** The target book version to upgrade to. */
  targetVersion: string;
  /** Book descriptor at the connection's current version (for scope comparison). */
  currentBook?: Bdkv2BookDescriptor;
  /** Book descriptor at the target version (for scope comparison). */
  latestBook?: Bdkv2BookDescriptor;
  /** Full connection descriptors for the book being upgraded. */
  connections: V2BookConnectionDescriptor[];
}

/**
 * Encapsulates the automation connection upgrade flow.
 *
 * Credential connections upgrade directly. OAuth connections whose scope changed
 * between the current and target book versions require the user to sign in again;
 * the upgrade then resumes automatically once the browser returns from the OAuth
 * redirect. Published automations are forked into a draft before upgrading.
 *
 * @param params - Automation id and draft flag
 * @returns `upgrade` callback and an `isUpgrading` flag
 */
export const useUpgradeAutomationConnection = ({
  automationId,
  isDraft,
}: IUseUpgradeAutomationConnectionParams) => {
  const { t: tAutomation } = useTranslation('automation');
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const organizationId = useUserPreferencesStore(selectCurrentOrganizationId);
  const workspaceId = useUserPreferencesStore(selectCurrentWorkspaceId);

  // No integration name/version so the OAuth referer returns to this automation.
  const { reconnectById } = useConnectionActions(organizationId, workspaceId);

  // Tracks the async tail of afterUpgrade (scope-fetch + reconnectById) which
  // runs after the upgradeBookConnection mutation resolves and is not covered by
  // isUpgradingMutation or isForking.
  const [isReauthing, setIsReauthing] = useState(false);

  // Tracks which book is currently being upgraded so callers can scope loading
  // state to the specific item that was triggered.
  const [upgradingBook, setUpgradingBook] = useState<string | null>(null);

  const { mutate: forkPublishedAutomation, isPending: isForking } =
    useForkPublishedAutomation();

  const { mutate: upgradeBookConnection, isPending: isUpgradingMutation } =
    useUpgradeAutomationBookConnectionVersion({
      mutation: {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: getExtractAutomationCodeConnectionsQueryKey(
              organizationId,
              workspaceId,
              automationId,
            ),
          });
        },
        onError: error => {
          // The upgrade is offered from a cached connections list, so the
          // version shown can be behind the server and the connection may
          // already be on the target version. The backend rejects that, but
          // nothing failed and nothing is left to upgrade: refresh the list so
          // the stale version clears, and report it as a no-op.
          if (isConnectionAlreadyAtTargetVersionError(error)) {
            queryClient.invalidateQueries({
              queryKey: getExtractAutomationCodeConnectionsQueryKey(
                organizationId,
                workspaceId,
                automationId,
              ),
            });
            toast.info(
              tAutomation('automation.integrations.upgradeAlreadyLatest'),
            );
            return;
          }

          captureException(
            error instanceof Error ? error : new Error(String(error)),
            { tags: { module: 'automation', action: 'upgradeConnection' } },
          );
          toast.error(tAutomation('automation.integrations.upgradeError'));
        },
      },
    });

  const runUpgrade = useCallback(
    (
      book: string,
      targetVersion: string,
      onSuccess?: (reauthConnectionIds: string[]) => void,
    ) => {
      const performUpgrade = (
        afterUpgrade?: (reauthConnectionIds: string[]) => void,
      ) => {
        upgradeBookConnection(
          {
            organization: organizationId,
            workspace: workspaceId,
            automation: automationId,
            data: { book, new_book_version: targetVersion },
          },
          {
            onSuccess: data => {
              // Keys in automation.connections are the book name optionally
              // suffixed with a numeric index (e.g. "sheets", "sheets1", "sheets2").
              // Match exactly: key === book OR key === book + digits only,
              // so "sheets-extended" is never matched when book is "sheets".
              const bookKeyPattern = new RegExp(`^${book}\\d*$`);
              const reauthIds = Object.entries(
                data?.data?.automation?.connections ?? {},
              )
                .filter(([key]) => bookKeyPattern.test(key))
                .map(([, conn]) => conn.connection_id)
                .filter((id): id is string => Boolean(id));
              afterUpgrade?.(reauthIds);
            },
          },
        );
      };

      if (isDraft) {
        performUpgrade(onSuccess);
        return;
      }

      // Published automations must be forked into a draft before upgrading.
      forkPublishedAutomation(
        {
          organization: organizationId,
          workspace: workspaceId,
          automation: automationId,
          data: {},
        },
        {
          onSuccess: () => performUpgrade(onSuccess),
          onError: error => {
            captureException(error, {
              tags: { module: 'automation', action: 'forkForUpgrade' },
            });
            toast.error(tAutomation('automation.integrations.upgradeError'));
          },
        },
      );
    },
    [
      isDraft,
      upgradeBookConnection,
      forkPublishedAutomation,
      organizationId,
      workspaceId,
      automationId,
      tAutomation,
    ],
  );

  const isUpgrading = isUpgradingMutation || isForking || isReauthing;

  const upgrade = useCallback(
    ({
      book,
      targetVersion,
      currentBook,
      latestBook,
      connections,
    }: IUpgradeParams) => {
      setUpgradingBook(book);
      // Collect connections whose OAuth scope changed between versions.
      // Checked here (before upgrade) so we know which connections to re-auth
      // after the upgrade completes.
      const connectionsNeedingReauth = connections.filter(connection =>
        isReauthRequiredForUpgrade(connection, currentBook, latestBook),
      );

      // All connections for the same book share the same version, so if any
      // one needs re-auth they all do. reauthConnectionIds from the backend
      // are the post-upgrade IDs to authorise — no separate fetch needed.
      const afterUpgrade = async (reauthConnectionIds: string[]) => {
        // For published automations, navigate to the draft created by the fork.
        if (!isDraft) {
          navigate(
            getRoutePath('DRAFT_EDITOR', {
              orgId: organizationId,
              workspaceId,
              draftId: automationId,
            }),
          );
        }

        if (
          connectionsNeedingReauth.length === 0 ||
          reauthConnectionIds.length === 0
        ) {
          toast.success(tAutomation('automation.integrations.upgradeSuccess'));
          return;
        }

        setIsReauthing(true);
        try {
          const [firstId, ...remainingIds] = reauthConnectionIds;
          writeUpgradeIntent({
            automationId,
            pendingReauthConnections: remainingIds.map(id => ({
              connection_id: id,
            })),
          });
          try {
            await reconnectById(firstId);
          } catch {
            clearUpgradeIntent();
            setIsReauthing(false);
          }
        } catch {
          toast.error(tAutomation('automation.integrations.upgradeError'));
          setIsReauthing(false);
        }
      };

      runUpgrade(book, targetVersion, afterUpgrade);
    },
    [
      runUpgrade,
      reconnectById,
      automationId,
      isDraft,
      navigate,
      organizationId,
      workspaceId,
      tAutomation,
      setIsReauthing,
    ],
  );

  const [searchParams, setSearchParams] = useSearchParams();

  // Guard against resumeUpgrade being invoked more than once in the same
  // component lifetime (e.g. if the effect fires before setSearchParams clears
  // ?data= from the URL).
  const isResumingRef = useRef(false);

  const resumeUpgrade = useCallback(async () => {
    if (isResumingRef.current) return;

    const intent = readUpgradeIntent();
    if (!intent || intent.automationId !== automationId) return;

    isResumingRef.current = true;

    if (intent.pendingReauthConnections.length > 0) {
      // More connections still need re-authentication. Pop the next one,
      // persist the remaining queue, then redirect for another sign-in.
      const [next, ...remaining] = intent.pendingReauthConnections;
      writeUpgradeIntent({ ...intent, pendingReauthConnections: remaining });
      try {
        await reconnectById(next.connection_id);
      } catch {
        clearUpgradeIntent();
      }
      return;
    }

    // All connections have been re-authenticated. The upgrade already ran before
    // re-auth started, so there is nothing else to do.
    clearUpgradeIntent();
    toast.success(tAutomation('automation.integrations.upgradeSuccess'));
  }, [automationId, reconnectById, tAutomation]);

  // Auto-resume upgrade when the browser returns from an OAuth redirect.
  // This hook lives in AutomationIntegrationsPanel, which is always mounted
  // regardless of whether the integrations popover is open, so this effect
  // fires even when the panel is closed
  // eslint-disable-next-line no-restricted-syntax -- reason: must detect OAuth return at panel level so upgrade resumes without user opening the popover
  useEffect(() => {
    // A managed-credential sign-in's return carries a credential's id.
    if (hasManagedOAuthReturn(searchParams)) return;
    const oauthConnectionId = searchParams.get('data');
    if (!oauthConnectionId || !hasPendingConnectionUpgrade(automationId)) {
      return;
    }
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('data');
    setSearchParams(nextParams, { replace: true });
    resumeUpgrade();
  }, [searchParams, setSearchParams, automationId, resumeUpgrade]);

  return {
    upgrade,
    resumeUpgrade,
    isUpgrading,
    upgradingBook: isUpgrading ? upgradingBook : null,
  };
};
